import Vue from 'vue'
import api from '@/apis/siteMember'
import parser from '@/models/siteMember'
import {
  RESET_STATE,
  PROCESS_API_SUCCESS,
  PROCESS_API_FAILURE,
  GET_SITE_MEMBER_REQUEST,
  GET_SITE_MEMBER_SUCCESS,
  GET_SITE_MEMBER_FAILURE,
  GET_SITE_MEMBERS_REQUEST,
  GET_SITE_MEMBERS_SUCCESS,
  GET_SITE_MEMBERS_FAILURE,
  CREATE_SITE_MEMBER_REQUEST,
  CREATE_SITE_MEMBER_SUCCESS,
  CREATE_SITE_MEMBER_FAILURE,
  DELETE_SITE_MEMBER_REQUEST,
  DELETE_SITE_MEMBER_SUCCESS,
  DELETE_SITE_MEMBER_FAILURE,
  UPDATE_SITE_MEMBER_REQUEST,
  UPDATE_SITE_MEMBER_SUCCESS,
  UPDATE_SITE_MEMBER_FAILURE,
  SEND_INVITE_EMAIL_TO_SITE_MEMBER_REQUEST,
  SEND_INVITE_EMAIL_TO_SITE_MEMBER_SUCCESS,
  SEND_INVITE_EMAIL_TO_SITE_MEMBER_FAILURE,
  GET_MEMBER_ROLES_REQUEST,
  GET_MEMBER_ROLES_SUCCESS,
  GET_MEMBER_ROLES_FAILURE,
  GET_PARTITION_MEMBERS_REQUEST,
  GET_PARTITION_MEMBERS_SUCCESS,
  GET_PARTITION_MEMBERS_FAILURE,
  ADD_PARTITION_MEMBER_REQUEST,
  ADD_PARTITION_MEMBER_SUCCESS,
  ADD_PARTITION_MEMBER_FAILURE,
  DELETE_PARTITION_MEMBER_REQUEST,
  DELETE_PARTITION_MEMBER_SUCCESS,
  DELETE_PARTITION_MEMBER_FAILURE,
  UPDATE_PARTITION_MEMBER_REQUEST,
  UPDATE_PARTITION_MEMBER_SUCCESS,
  UPDATE_PARTITION_MEMBER_FAILURE,
} from '@/store/mutation-types'

let initialState = {
  siteId: null,
  members: null,
  siteMemberMePermission: null,
  memberRoles: null,
  memberRoleActions: null,
  partitionMembers: null,
  status: {
    getMember: null,
    getMembers: null,
    createMember: null,
    deleteMember: null,
    updateMember: null,
    sendInviteEmail: null,
    getMemberRoles: null,
    getPartitionMembers: null,
    addPartitionMember: null,
    deletePartitionMember: null,
    updatePartitionMember: null,
  }
}

// initial state
const state = Vue.util.extend({}, initialState)

// getters
const getters = {
  memberRoles: function (state) {
    return state.memberRoles || []
  },
  memberRoleActions: function (state) {
    return state.memberRoleActions || []
  },
  siteMember: function (state, getters) {
    return function (memberId) {
      return getters.siteMembers[memberId]
    }
  },
  siteMembers: function (state) {
    return state.members || {}
  },
  siteMemberList: function (state) {
    return Object.keys(state.members)
  },
  statusGetSiteMember: function (state) {
    return state.status.getMember
  },
  statusGetSiteMembers: function (state) {
    return state.status.getMembers
  },
  statusCreateSiteMember: function (state) {
    return state.status.createMember
  },
  statusDeleteSiteMember: function (state) {
    return state.status.deleteMember
  },
  statusUpdateSiteMember: function (state) {
    return state.status.updateMember
  },
  statusSendInviteEmailToSiteMember: function (state) {
    return state.status.sendInviteEmail
  },
  statusMemberRoles: function (state) {
    return state.status.getMemberRoles
  },
  siteMemberMePermission: function (state) {
    return state.siteMemberMePermission
  },
  // Camera Bypass permission (resource 13). mode: 0=none, 1=view only, 2=full.
  // canBypassCameras gates the bypass/clear ACTIONS; canViewBypass gates the
  // status badge (view-only roles still see it, mode-0 roles don't).
  canBypassCameras: function (state) {
    return Number(state.siteMemberMePermission?.bypass) === 2
  },
  canViewBypass: function (state) {
    return Number(state.siteMemberMePermission?.bypass) >= 1
  },
  // Approval permission (resource 14): view + respond to monitoring-center
  // approval requests. Gates the approval dialog and approve/decline actions.
  canRespondApproval: function (state) {
    return Number(state.siteMemberMePermission?.approval) === 2
  },
  partitionMembers: function (state) {
    return state.partitionMembers || []
  },
  statusGetPartitionMembers: function (state) {
    return state.status.getPartitionMembers
  },
  // 특정 파티션에서 내 role의 eventVideo(resource=3) mode를 반환
  // 0=no, 1=view only, 2=full. 정보 없으면 null.
  myPartitionEventVideoMode: function (state, getters) {
    return function (partitionId) {
      if (partitionId == null) return null
      const myId = getters.myUserId
      const members = state.partitionMembers
      if (!Array.isArray(members)) return null
      const me = members.find(m => Number(m.user_id) === Number(myId) && Number(m.partition_id) === Number(partitionId))
      if (!me) return null
      const roles = state.memberRoles
      if (!Array.isArray(roles)) return null
      const role = roles.find(r => Number(r.id) === Number(me.role_id))
      if (!role || !Array.isArray(role.table)) return null
      const eventVideo = role.table.find(t => Number(t.resource) === 3)
      return eventVideo ? eventVideo.mode : null
    }
  },
  // 특정 파티션에서 내 role의 arming(resource=1) mode를 반환
  // 0=no, 1=view only, 2=full. 정보 없으면 null.
  // arming 상태를 "변경"하려면 mode === 2(full)가 필요하다.
  myPartitionArmingMode: function (state, getters) {
    return function (partitionId) {
      if (partitionId == null) return null
      const myId = getters.myUserId
      const members = state.partitionMembers
      if (!Array.isArray(members)) return null
      const me = members.find(m => Number(m.user_id) === Number(myId) && Number(m.partition_id) === Number(partitionId))
      if (!me) return null
      const roles = state.memberRoles
      if (!Array.isArray(roles)) return null
      const role = roles.find(r => Number(r.id) === Number(me.role_id))
      if (!role || !Array.isArray(role.table)) return null
      const arming = role.table.find(t => Number(t.resource) === 1)
      return arming ? arming.mode : null
    }
  },
}

// actions
const actions = {
  getSiteMember: function ({commit}, {siteId, memberId}) {
    commit(GET_SITE_MEMBER_REQUEST)
    return new Promise((resolve, reject) => {
      api.getSiteMember({siteId, memberId}).then(
        res => {
          let data = res.body[0]
          commit(GET_SITE_MEMBER_SUCCESS, {data})
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_MEMBER_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getSiteMembers: function ({commit, getters}, {siteId}) {
    commit(GET_SITE_MEMBERS_REQUEST, {siteId})
    return new Promise((resolve, reject) => {
      api.getSiteMembers({siteId}).then(
        res => {
          let data = res.body
          let myUserId = getters.myUserId
          commit(GET_SITE_MEMBERS_SUCCESS, {data, myUserId})
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_SITE_MEMBERS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  // [0:OWNER, 1:ADMIN, 2:MEMBER]
  createSiteMember: function ({commit}, {siteId, email, role, firstName, lastName}) {
    commit(CREATE_SITE_MEMBER_REQUEST)
    return new Promise((resolve, reject) => {
      api.createSiteMember({siteId, email, role, firstName, lastName}).then(
        res => {
          commit(CREATE_SITE_MEMBER_SUCCESS)
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(CREATE_SITE_MEMBER_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  deleteSiteMember: function ({commit}, {siteId, memberId}) {
    commit(DELETE_SITE_MEMBER_REQUEST)
    return new Promise((resolve, reject) => {
      api.deleteSiteMember({siteId, memberId}).then(
        res => {
          commit(DELETE_SITE_MEMBER_SUCCESS, {siteId, memberId})
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(DELETE_SITE_MEMBER_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  updateSiteMember: function ({commit}, {siteId, memberId, role}) {
    commit(UPDATE_SITE_MEMBER_REQUEST)
    return new Promise((resolve, reject) => {
      api.updateSiteMember({siteId, memberId, role}).then(
        res => {
          commit(UPDATE_SITE_MEMBER_SUCCESS)
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(UPDATE_SITE_MEMBER_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  sendInviteEmailToSiteMember: function ({commit}, {siteId, memberId}) {
    commit(SEND_INVITE_EMAIL_TO_SITE_MEMBER_REQUEST)
    return new Promise((resolve, reject) => {
      api.sendInviteEmailToSiteMember({siteId, memberId}).then(
        res => {
          commit(SEND_INVITE_EMAIL_TO_SITE_MEMBER_SUCCESS)
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(SEND_INVITE_EMAIL_TO_SITE_MEMBER_FAILURE)
          reject(new Error(err.body.message))
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getMemberRoles: function ({commit}) {
    commit(GET_MEMBER_ROLES_REQUEST)
    return new Promise((resolve, reject) => {
      api.getMemberRoles().then(
        res => {
          let data = res.body
          commit(GET_MEMBER_ROLES_SUCCESS, data)
          commit(PROCESS_API_SUCCESS)
          resolve()
        },
        err => {
          commit(GET_MEMBER_ROLES_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  getPartitionMembers: function ({commit}, {siteId}) {
    commit(GET_PARTITION_MEMBERS_REQUEST)
    return new Promise((resolve, reject) => {
      api.getPartitionMembers({siteId}).then(
        res => {
          commit(GET_PARTITION_MEMBERS_SUCCESS, {data: res.body})
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(GET_PARTITION_MEMBERS_FAILURE)
          reject({
            status: err.status,
            statusText: err.body,
          })
          commit(PROCESS_API_FAILURE, {
            status: err.status,
            statusText: err.body,
            origin: window.location.origin,
            err: Vue.tool.parseToStringify(err),
          })
        }
      )
    })
  },
  addPartitionMembers: function ({commit}, {siteId, siteMemberId, partitions}) {
    commit(ADD_PARTITION_MEMBER_REQUEST)
    return new Promise((resolve, reject) => {
      api.addPartitionMembers({siteId, siteMemberId, partitions}).then(
        res => {
          commit(ADD_PARTITION_MEMBER_SUCCESS)
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(ADD_PARTITION_MEMBER_FAILURE)
          reject({ status: err.status, statusText: err.body })
          commit(PROCESS_API_FAILURE, { status: err.status, statusText: err.body, origin: window.location.origin, err: Vue.tool.parseToStringify(err) })
        }
      )
    })
  },
  deletePartitionMembers: function ({commit}, {siteId, siteMemberId, partitionIds}) {
    commit(DELETE_PARTITION_MEMBER_REQUEST)
    return new Promise((resolve, reject) => {
      api.deletePartitionMembers({siteId, siteMemberId, partitionIds}).then(
        res => {
          commit(DELETE_PARTITION_MEMBER_SUCCESS)
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(DELETE_PARTITION_MEMBER_FAILURE)
          reject({ status: err.status, statusText: err.body })
          commit(PROCESS_API_FAILURE, { status: err.status, statusText: err.body, origin: window.location.origin, err: Vue.tool.parseToStringify(err) })
        }
      )
    })
  },
  updatePartitionMembers: function ({commit}, {siteId, siteMemberId, partitions}) {
    commit(UPDATE_PARTITION_MEMBER_REQUEST)
    return new Promise((resolve, reject) => {
      api.updatePartitionMembers({siteId, siteMemberId, partitions}).then(
        res => {
          commit(UPDATE_PARTITION_MEMBER_SUCCESS)
          resolve(res)
          commit(PROCESS_API_SUCCESS)
        },
        err => {
          commit(UPDATE_PARTITION_MEMBER_FAILURE)
          reject({ status: err.status, statusText: err.body })
          commit(PROCESS_API_FAILURE, { status: err.status, statusText: err.body, origin: window.location.origin, err: Vue.tool.parseToStringify(err) })
        }
      )
    })
  },
}

// mutations
const mutations = {
  [RESET_STATE]: function (state) {
    for (let f in state) {
      Vue.set(state, f, initialState[f])
    }
  },
  [GET_SITE_MEMBER_REQUEST]: function (state) {
    state.status.getMember = "requested"
  },
  [GET_SITE_MEMBER_SUCCESS]: function (state, {data}) {
    let memberInfo = parser.parseMemberData(data)

    // add role name from roles matched,
    if (!state.memberRoles) return
    if (!(state.memberRoles instanceof Array)) return
    for (let i = 0; i < state.memberRoles?.length; i++) {
      const action = state.memberRoles[i];
      if (action.id == memberInfo.permissionInfo.role) {
        memberInfo.permissionInfo.roleName = action.name
        break
      }
    }

    if (memberInfo.memberId) {
      state.members = {...state.members, [memberInfo.memberId + '']: memberInfo}
    }
    state.status.getMember = "successful"
  },
  [GET_SITE_MEMBER_FAILURE]: function (state) {
    state.status.getMember = "failed"
  },
  [GET_SITE_MEMBERS_REQUEST]: function (state, {siteId}) {
    // RESET_STATE - site or dateKey changed
    if (state.siteId && state.siteId !== siteId) {
      for (let f in state) {
        ////////////////////////////////////////////
        // member roles is need to keep stored!! 
        // becuase it is global level data.
        ////////////////////////////////////////////
        if (f === 'memberRoles') continue
        else if (f === 'memberRoleActions') continue

        // RESET
        Vue.set(state, f, initialState[f])
      }
    }
    state.siteId = siteId
    state.status.getMembers = "requested"
  },
  [GET_SITE_MEMBERS_SUCCESS]: function (state, {data, myUserId}) {
    if (!(state.members instanceof Object)) state.members = {}

    let members = {}
    let roles = {}

    data.map(memberData => {
      let memberInfo = parser.parseMemberData(memberData)
      
      // add role name from roles matched,
      if (!state.memberRoles) return
      if (!(state.memberRoles instanceof Array)) return
      for (let i = 0; i < state.memberRoles?.length; i++) {
        const action = state.memberRoles[i];
        if (action.id == memberInfo.permissionInfo.role) {
          memberInfo.permissionInfo.roleName = action.name
          break
        }
      }

      if (memberInfo.memberId) {
        members[memberInfo.memberId + ''] = memberInfo
      }
    })

    let myRoleId = members[myUserId]?.permissionInfo?.role

    // 0: no permission
    // 1: read
    // 2: read, edit
    for (let i = 0; i < state.memberRoles?.length; i++) {
      const role = state?.memberRoles[i];
      if (role.id == myRoleId) {

        roles.role = myRoleId
        
        for (let ii = 0; ii < role.table?.length; ii++) {
          const action = role.table[ii];
          if (action.resource == 1) roles['arming'] = action.mode
          else if (action.resource == 2) roles['liveView'] = action.mode
          else if (action.resource == 3) roles['eventVideo'] = action.mode
          else if (action.resource == 4) roles['talkdown'] = action.mode
          else if (action.resource == 5) roles['relay'] = action.mode
          else if (action.resource == 6) roles['privacy'] = action.mode
          else if (action.resource == 7) roles['member'] = action.mode
          else if (action.resource == 8) roles['armingSchedule'] = action.mode
          else if (action.resource == 9) roles['incidentReport'] = action.mode
          else if (action.resource == 10) roles['ptz'] = action.mode
          else if (action.resource == 11) roles['listenAudio'] = action.mode
          else if (action.resource == 12) roles['cameraRelay'] = action.mode
          else if (action.resource == 13) roles['bypass'] = action.mode
          else if (action.resource == 14) roles['approval'] = action.mode
        }
      }
    }

    Vue.set(state, 'siteMemberMePermission', roles)
    Vue.set(state, 'members', members)
    state.status.getMembers = "successful"
  },
  [GET_SITE_MEMBERS_FAILURE]: function (state) {
    state.status.getMembers = "failed"
  },
  [CREATE_SITE_MEMBER_REQUEST]: function (state) {
    state.status.createMember = "requested"
  },
  [CREATE_SITE_MEMBER_SUCCESS]: function (state) {
    state.status.createMember = "successful"
  },
  [CREATE_SITE_MEMBER_FAILURE]: function (state) {
    state.status.createMember = "failed"
  },
  [DELETE_SITE_MEMBER_REQUEST]: function (state) {
    state.status.deleteMember = "requested"
  },
  [DELETE_SITE_MEMBER_SUCCESS]: function (state, {memberId}) {
    if (state.members[memberId]) delete state.members[memberId]
    state.status.deleteMember = "successful"
  },
  [DELETE_SITE_MEMBER_FAILURE]: function (state) {
    state.status.deleteMember = "failed"
  },
  [UPDATE_SITE_MEMBER_REQUEST]: function (state) {
    state.status.updateMember = "requested"
  },
  [UPDATE_SITE_MEMBER_SUCCESS]: function (state) {
    state.status.updateMember = "successful"
  },
  [UPDATE_SITE_MEMBER_FAILURE]: function (state) {
    state.status.updateMember = "failed"
  },
  [SEND_INVITE_EMAIL_TO_SITE_MEMBER_REQUEST]: function (state) {
    state.status.updateMember = "requested"
  },
  [SEND_INVITE_EMAIL_TO_SITE_MEMBER_SUCCESS]: function (state) {
    state.status.updateMember = "successful"
  },
  [SEND_INVITE_EMAIL_TO_SITE_MEMBER_FAILURE]: function (state) {
    state.status.updateMember = "failed"
  },
  [GET_MEMBER_ROLES_REQUEST]: function (state) {
    state.status.getMemberRoles = 'requested'
  },
  [GET_MEMBER_ROLES_SUCCESS]: function (state, data) {
    let rolesInfo = parser.parseMenberRolesData(data)

    state.memberRoleActions = rolesInfo.actions
    state.memberRoles = rolesInfo.roles

    state.status.getMemberRoles = 'successful'
  },
  [GET_MEMBER_ROLES_FAILURE]: function (state) {
    state.status.getMemberRoles = 'failed'
  },
  [GET_PARTITION_MEMBERS_REQUEST]: function (state) {
    state.status.getPartitionMembers = 'requested'
  },
  [GET_PARTITION_MEMBERS_SUCCESS]: function (state, {data}) {
    Vue.set(state, 'partitionMembers', data)
    state.status.getPartitionMembers = 'successful'
  },
  [GET_PARTITION_MEMBERS_FAILURE]: function (state) {
    state.status.getPartitionMembers = 'failed'
  },
  [ADD_PARTITION_MEMBER_REQUEST]: function (state) {
    state.status.addPartitionMember = 'requested'
  },
  [ADD_PARTITION_MEMBER_SUCCESS]: function (state) {
    state.status.addPartitionMember = 'successful'
  },
  [ADD_PARTITION_MEMBER_FAILURE]: function (state) {
    state.status.addPartitionMember = 'failed'
  },
  [DELETE_PARTITION_MEMBER_REQUEST]: function (state) {
    state.status.deletePartitionMember = 'requested'
  },
  [DELETE_PARTITION_MEMBER_SUCCESS]: function (state) {
    state.status.deletePartitionMember = 'successful'
  },
  [DELETE_PARTITION_MEMBER_FAILURE]: function (state) {
    state.status.deletePartitionMember = 'failed'
  },
  [UPDATE_PARTITION_MEMBER_REQUEST]: function (state) {
    state.status.updatePartitionMember = 'requested'
  },
  [UPDATE_PARTITION_MEMBER_SUCCESS]: function (state) {
    state.status.updatePartitionMember = 'successful'
  },
  [UPDATE_PARTITION_MEMBER_FAILURE]: function (state) {
    state.status.updatePartitionMember = 'failed'
  },
}

export default {
  state,
  getters,
  actions,
  mutations
}
